import {
  type PropsWithChildren,
  createContext,
  useContext,
  useState
} from 'react';

export enum ModalType {
  AddCardToEnroll = 'AddCardToEnroll',
  AddCardToSplitGuest = 'AddCardToSplitGuest',
  AddCardToSplitLead = 'AddCardToSplitLead',
  AddToCalendar = 'AddToCalendar',
  BookingFields = 'BookingFields',
  CancelReservation = 'CancelReservation',
  // NOTE: All Modals that begin with Checkless are v1 and will be removed at some point in the future
  // Any net new checkless modal should not use that as a prefix
  ChecklessAddCard = 'ChecklessAddCard',
  ChecklessAddTip = 'ChecklessAddTip',
  ChecklessEnrollmentComplete = 'ChecklessEnrollmentComplete',
  ChecklessEditCard = 'ChecklessEditCard',
  ChecklessOtp = 'ChecklessOtp',
  ChecklessRemoveCard = 'ChecklessRemoveCard',
  ChecklessReviewUnsavedCard = 'ChecklessReviewUnsavedCard',
  ChecklessSelectCard = 'ChecklessSelectCard',
  ChecklessUpdateCard = 'ChecklessUpdateCard',
  ChecklessUpdateTip = 'ChecklessUpdateTip',
  ConfirmReservation = 'ConfirmReservation',
  GetHelp = 'GetHelp',
  InviteToSplit = 'InviteToSplit',
  LeaveParty = 'LeaveParty',
  LeaveWaitlist = 'LeaveWaitlist',
  ManageGuests = 'ManageGuests',
  ManageReservation = 'ManageReservation',
  ManageSplitBill = 'ManageSplitBill',
  ModifyGuests = 'ModifyGuests',
  RemoveCard = 'RemoveCard',
  RemoveGuest = 'RemoveGuest',
  SelectGratuity = 'SelectGratuity',
  SpecialOccasion = 'SpecialOccasion',
  SpecialRequest = 'SpecialRequest',
  UpdateCard = 'UpdateCard',
  ViewGuests = 'ViewGuests',
  ViewSplitBill = 'ViewSplitBill',
  WaitlistEstimate = 'WaitlistEstimate'
}

export interface ModalContextInterface {
  isOpen: boolean;
  open: (modalType: ModalType, modalContext?: Record<string, any>) => void;
  back: () => void;
  close: () => void;
  currentModal: ModalType | null;
  modalStack: ModalType[];
  modalContext: Record<string, any>;
  setModalContext: (context: object) => void;
}

export const ModalContext = createContext<ModalContextInterface | null>(null);

export const ModalProvider = ({ children }: PropsWithChildren) => {
  const [isOpen, setIsOpen] = useState(false);
  const [modalStack, setModalStack] = useState<ModalType[]>([]);
  const [modalContext, setModalContext] = useState({});
  const currentModal = modalStack.at(-1) || null;

  // TODO: Right now we're losing context when we move back. That isn't an issue so far as most modals don't
  // need context. If this changes in the future, this logic will need updating so context is tracked in the stack
  const open = (modalType: ModalType, context: Record<string, any> = {}) => {
    const { disableBack } = context;
    setModalStack(prev => (disableBack ? [modalType] : [...prev, modalType]));
    setModalContext(context);
    setIsOpen(true);
  };
  const close = () => {
    setIsOpen(false);
    setModalContext({});
    setModalStack([]);
  };
  const back = () => setModalStack(prev => prev.slice(0, -1));

  return (
    <ModalContext.Provider
      value={{
        open,
        close,
        back,
        setModalContext,
        isOpen,
        currentModal,
        modalStack,
        modalContext
      }}
    >
      {children}
    </ModalContext.Provider>
  );
};

export const useModal = () => {
  const context = useContext(ModalContext);
  if (context == null) {
    throw new Error('useModal must be used within ModalProvider');
  }

  return context;
};
